"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";

interface ProductCategoryProps {
  data: {
    id: number;
    title?: string;
    description?: string;
    isLive: boolean;
    useH1Tag: boolean;
    Card: {
      id: number;
      title?: string;
      description?: string;
      href?: string;
      screenDisplay: boolean;
      tag?: string;
      image?: {
        data?: {
          attributes?: {
            url: string;
            alternativeText: string | null;
          };
        };
      };
    }[];
  };
}

const ShopByCircularOccasion = ({ data }: ProductCategoryProps) => {
  if (!data?.isLive) return null;

  const titleParts = data?.title?.split(" ") || [];
  const firstPart = titleParts.slice(0, 2).join(" ") || "Shop By";
  const restPart = titleParts.slice(2).join(" ") || "";

  const cards = data?.Card?.filter((c) => c?.screenDisplay) || [];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        {data?.title && (
          <div className="flex flex-col items-center mb-16">
            <div className="text-3xl md:text-5xl font-black text-gray-900 uppercase italic tracking-tighter text-center leading-none">
              {data.useH1Tag ? (
                <h1>
                  {firstPart}{" "}
                  <span style={{ color: "var(--secondary)" }}>
                    {restPart}
                  </span>
                </h1>
              ) : (
                <h2>
                  {firstPart}{" "}
                  <span style={{ color: "var(--secondary)" }}>
                    {restPart}
                  </span>
                </h2>
              )}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <span className="h-1 w-8 bg-[var(--secondary)]" />
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-700">
                {data?.description || "Curated Styles"}
              </span>
              <span className="h-1 w-8 bg-[var(--secondary)]" />
            </div>
          </div>
        )}

        {/* Circular Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-12">
          {cards.map((item, index) => {
            const imageUrl = item?.image?.data?.attributes?.url;

            return (
              <div
                key={item?.id || index}
                className="group flex flex-col items-center cursor-pointer"
              >
                <Link
                  href={item?.href || "#"}
                  className="w-full flex flex-col items-center"
                >
                  {/* Circle */}
                  <div className="relative w-full aspect-square rounded-full overflow-hidden border-2 border-transparent group-hover:border-[var(--secondary)] transition-all duration-300 p-1">
                    <div className="w-full h-full rounded-full overflow-hidden relative">
                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt={item?.title || "Occasion"}
                          fill
                          quality={80}
                          sizes="(max-width: 768px) 50vw, 16vw"
                          className="object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-neutral-100" />
                      )}

                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="mt-6 flex flex-col items-center">
                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest text-center">
                      {item?.title}
                    </h3>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-1">
                      <span
                        className="text-[9px] font-bold uppercase"
                        style={{ color: "var(--secondary)" }}
                      >
                        View All
                      </span>
                      <ChevronRight
                        className="h-3 w-3"
                        style={{ color: "var(--secondary)" }}
                      />
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ShopByCircularOccasion;
